import * as Sentry from '@sentry/react';
import React from 'react';

// Type declaration for Sentry loader script
declare global {
  interface Window {
    Sentry?: {
      init: (config: any) => void;
      onLoad?: (callback: (Sentry: any) => void) => void;
    };
  }
}

export const initSentry = async (dsn: string) => {
  // Determine environment from build-time variables or import.meta.env
  const environment = typeof __APP_ENVIRONMENT__ !== 'undefined' 
    ? __APP_ENVIRONMENT__ 
    : import.meta.env.MODE || 'development';

  // Extract DSN components for loader script
  const dsnUrl = new URL(dsn);
  const publicKey = dsnUrl.username;
  const host = dsnUrl.hostname;
  const projectId = dsnUrl.pathname.split('/')[1];

  // Try using loader script first (loads SDK from Sentry domain, may avoid CORS)
  const useLoaderScript = import.meta.env.VITE_SENTRY_USE_LOADER !== 'false';
  
  if (useLoaderScript && publicKey && host && projectId) {
    return new Promise<void>((resolve, reject) => {
      // Load the Sentry loader script
      const script = document.createElement('script');
      script.src = `https://${host}/js-sdk-loader/${publicKey}.min.js`;
      script.crossOrigin = 'anonymous';
      script.async = true;
      
      script.onload = () => {
        // The loader script loads Sentry SDK from Sentry's domain
        // We'll use the npm package for initialization but the loader script
        // may help with CORS since the SDK is loaded from Sentry's domain
        // Wait a bit for the script to fully initialize
        setTimeout(() => {
          initWithNpmPackage(dsn, environment);
          resolve();
        }, 100);
      };
      
      script.onerror = (error) => {
        console.error('[Sentry] Failed to load loader script:', error);
        initWithNpmPackage(dsn, environment);
        resolve();
      };
      
      document.head.appendChild(script);
    });
  } else {
    // Use npm package directly
    initWithNpmPackage(dsn, environment);
    return Promise.resolve();
  }
};

const initWithNpmPackage = (dsn: string, environment: string) => {

  Sentry.init({
    dsn,
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,
    environment,
    // Disable profiling in development to avoid document policy violations
    profilesSampleRate: environment === 'development' ? 0 : 1.0,
    // Capture unhandled promise rejections
    captureUnhandledRejections: true,
    // Capture uncaught exceptions
    beforeSend(event, hint) {
      // Log to console in development for debugging
      if (environment === 'development') {
        console.log('[Sentry] Attempting to send event:', event.type || 'unknown');
      }
      // Don't filter out any events - send everything
      return event;
    },
    // Use default transport - custom transport removed to avoid conflicts
    // CORS errors will be logged via beforeSend and error handlers
    tracePropagationTargets: [
      'localhost', 
      'dev-api.clearminds.se',
      /^https:\/\/dev-api\.nodus\.foo\/api/
    ],
    integrations: [
      // Browser tracing integration - enabled with document policy allowing profiling
      // The document policy meta tag in index.html allows js-profiling
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      // Enable profiling integration (document policy meta tag allows it)
      Sentry.browserProfilingIntegration(),
    ],
  });
};

export const enableSessionRecording = () => {
  Sentry.addBreadcrumb({
    category: 'session',
    message: 'Session recording enabled',
    level: 'info'
  });
};

export const pauseSessionRecording = () => {
  Sentry.addBreadcrumb({
    category: 'session',
    message: 'Session recording paused',
    level: 'info'
  });
};

export const resumeSessionRecording = () => {
  Sentry.addBreadcrumb({
    category: 'session',
    message: 'Session recording resumed',
    level: 'info'
  });
};

export const stopSessionRecording = () => {
  Sentry.addBreadcrumb({
    category: 'session',
    message: 'Session recording stopped',
    level: 'info'
  });
  
  disableSessionRecording();
};

export const disableSessionRecording = () => {
  Sentry.addBreadcrumb({
    category: 'session',
    message: 'Session recording disabled',
    level: 'info'
  });
};

export const trackPageView = (url: string, title: string) => {
  Sentry.addBreadcrumb({
    category: 'navigation',
    message: `Page view: ${title}`,
    data: { url, title },
    level: 'info'
  });
};

const FallbackComponent = () => {
  return React.createElement('div', { 
    className: "flex flex-col items-center justify-center min-h-screen p-6 bg-background" 
  }, React.createElement('div', { 
    className: "w-full max-w-md p-6 space-y-6 bg-card rounded-lg shadow-md border border-border" 
  }, [
    React.createElement('h2', { 
      key: 'title', 
      className: "text-2xl font-bold text-destructive" 
    }, "Something went wrong"),
    React.createElement('p', { 
      key: 'message',
      className: "text-muted-foreground" 
    }, "An error occurred and has been reported to our team."),
    React.createElement('button', { 
      key: 'button',
      className: "bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md",
      onClick: () => window.location.reload() 
    }, "Refresh the page")
  ]));
};

export const setUserContext = (user: { id: string; email: string } | null) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
    
    Sentry.addBreadcrumb({
      category: 'auth',
      message: `Authenticated user ${user.email}`,
      level: 'info',
    });
  } else {
    Sentry.setUser(null);
    
    Sentry.addBreadcrumb({
      category: 'auth',
      message: 'User logged out',
      level: 'info',
    });
  }
};

export const setCustomerContext = (customer: { id: string; name: string } | null) => {
  if (customer) {
    Sentry.setTag('customerId', customer.id);
    Sentry.setTag('customerName', customer.name);
    
    Sentry.setContext('customer', {
      id: customer.id,
      name: customer.name,
    });
    
    Sentry.addBreadcrumb({
      category: 'customer',
      message: `Selected customer: ${customer.name}`,
      level: 'info',
      data: {
        customerId: customer.id
      }
    });
  } else {
    Sentry.setTag('customerId', null);
    Sentry.setTag('customerName', null);
    Sentry.setContext('customer', null);
  }
};

export const captureException = (error: any, context?: Record<string, any>) => {
  Sentry.captureException(error, {
    contexts: context ? { additionalContext: context } : undefined
  });
};

export const SentryErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  return React.createElement(
    Sentry.ErrorBoundary,
    { 
      fallback: React.createElement(FallbackComponent),
      showDialog: true
    },
    children
  );
};

export default Sentry;
